import { createAsyncThunk } from '@reduxjs/toolkit';
import { BikeAllService } from '../../epics/sell/all/bike';
import { CarAllService } from '../../epics/sell/all/car';
import { CommercialAllService } from '../../epics/sell/all/commercial';
import { BaseAndArchiveService } from '../../epics/sell/base';
import { BikeService } from '../../epics/sell/bike';
import { CarService } from '../../epics/sell/car';
import { CommercialService } from '../../epics/sell/commercial';
import {
    addBikeKey,
    addBikeProKey,
    addCarKey,
    addCarProKey,
    addCommercialKey,
    addCommercialProKey,
    archiveListKey,
    basePoliceListKey,
    bikeAllKey,
    bikeAllListKey,
    bikeNoAccountKey,
    bikeNoAccountListKey,
    carAllKey,
    carAllListKey,
    carNoAccountKey,
    carNoAccountListKey,
    clearBikeKey,
    clearCarKey,
    clearCommercialKey,
    commercialAllKey,
    commercialAllListKey,
    commercialNoAccountKey,
    commercialNoAccountListKey,
    deleteBikeKey,
    deleteCarKey,
    deleteCommercialKey,
    editBikeKey,
    editBikeProKey,
    editCarKey,
    editCarProKey,
    editCommercialKey,
    editCommercialProKey,
    getBikeKey,
    getBikeListKey,
    getCarKey,
    getCarListKey,
    getCommercialKey,
    getCommercialListKey,
    updateBikeKey,
    updateCarKey,
    updateCommercialKey,
} from './sellKeys';
import { removeCache } from '../cache';
import { createAsyncThunkWithCaching } from '../thunkWrapper';
import { clearBikeMark } from '../common/bike/mark';
import { clearBikeModel } from '../common/bike/model';
import { clearBikeBodyType } from '../common/bike/bodyType';
import { clearBikeModification } from '../common/bike/modification';
import { clearBikeTechData } from '../common/bike/techData';
import { clearCarModel } from '../common/car/model';
import { clearCarBodyType } from '../common/car/bodyType';
import { clearCarGeneration } from '../common/car/generation';
import { clearCarModification } from '../common/car/modification';
import { clearCommercialBodyType } from '../common/commercial/bodyType';
import { clearCommercialModel } from '../common/commercial/model';
import { clearCommercialMark } from '../common/commercial/mark';
import { clearCommercialModification } from '../common/commercial/modification';
import { clearCarTechData } from '../common/car/techData';
import { clearCommercialTechData } from '../common/commercial/techData';
import {
    createServiceThunk,
    createAddThunk,
    createClearAction,
} from '../thunkFactory';

export const getArchiveList = createServiceThunk(
    archiveListKey,
    (params) => BaseAndArchiveService.getArchiveList(params),
);

export const getBasePoliceList = createServiceThunk(
    basePoliceListKey,
    (params) => BaseAndArchiveService.getBasePoliceList(params),
);

// Bike
export const clearBikeDataAction = createClearAction(clearBikeKey, [
    clearBikeMark,
    clearBikeModel,
    clearBikeBodyType,
    clearBikeModification,
    clearBikeTechData,
]);

export const addBike = createAddThunk(addBikeKey, (v) => BikeService.addBike(v));
export const addBikePro = createAddThunk(addBikeProKey, (v) => BikeService.addBikePro(v));
export const getBike = createServiceThunk(getBikeKey, (id) => BikeService.getBike(id));
export const getBikeList = createServiceThunk(getBikeListKey, () => BikeService.getBikeList());
export const deleteBike = createServiceThunk(deleteBikeKey, (id) => BikeService.deleteBike(id));
export const editBike = createServiceThunk(editBikeKey, (u) => BikeService.editBike(u));
export const editBikePro = createServiceThunk(editBikeProKey, (u) => BikeService.editBikePro(u));
export const updateBike = createServiceThunk(updateBikeKey, (u) => BikeService.updateBike(u));
export const getBikeAllList = createServiceThunk(bikeAllListKey, (p) => BikeAllService.getBikeAllList(p));
export const getBikeAll = createServiceThunk(bikeAllKey, (id) => BikeAllService.getBikeAll(id));
export const getBikeNoAccountList = createServiceThunk(bikeNoAccountListKey, (p) => BikeAllService.getBikeNoAccountList(p));
export const getBikeNoAccount = createServiceThunk(bikeNoAccountKey, (id) => BikeAllService.getBikeNoAccount(id));

// Car
export const clearCarDataAction = createClearAction(clearCarKey, [
    clearCarModel,
    clearCarBodyType,
    clearCarGeneration,
    clearCarModification,
    clearCarTechData,
]);

export const addCar = createAddThunk(addCarKey, (v) => CarService.addCar(v));
export const addCarPro = createAddThunk(addCarProKey, (v) => CarService.addCarPro(v));
export const getCar = createServiceThunk(getCarKey, (id) => CarService.getCar(id));
export const getCarList = createServiceThunk(getCarListKey, (p) => CarService.getCarList(p));

export const deleteCar = createAsyncThunk(
    deleteCarKey,
    async (id, thunkAPI) => {
        try {
            const response = await CarService.deleteCar(id);
            thunkAPI.dispatch(removeCache({ key: deleteCarKey }));
            return response;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    },
);

export const editCar = createServiceThunk(editCarKey, (u) => CarService.editCar(u));
export const editCarPro = createServiceThunk(editCarProKey, (u) => CarService.editCarPro(u));
export const updateCar = createServiceThunk(updateCarKey, (u) => CarService.updateCar(u));
export const getCarAllList = createServiceThunk(carAllListKey, (p) => CarAllService.getCarAllList(p));
export const getCarAll = createServiceThunk(carAllKey, (id) => CarAllService.getCarAll(id));
export const getCarNoAccountList = createServiceThunk(carNoAccountListKey, (p) => CarAllService.getCarNoAccountList(p));
export const getCarNoAccount = createServiceThunk(carNoAccountKey, (id) => CarAllService.getCarNoAccount(id));

// Commercial
export const clearCommercialDataAction = createClearAction(clearCommercialKey, [
    clearCommercialMark,
    clearCommercialModel,
    clearCommercialBodyType,
    clearCommercialModification,
    clearCommercialTechData,
]);

export const addCommercial = createAddThunk(addCommercialKey, (v) => CommercialService.addCommercial(v));
export const addCommercialPro = createAddThunk(addCommercialProKey, (v) => CommercialService.addCommercialPro(v));
export const getCommercial = createServiceThunk(getCommercialKey, (id) => CommercialService.getCommercial(id));
export const getCommercialList = createServiceThunk(getCommercialListKey, (p) => CommercialService.getCommercialList(p));
export const deleteCommercial = createServiceThunk(deleteCommercialKey, (id) => CommercialService.deleteCommercial(id));
export const editCommercial = createServiceThunk(editCommercialKey, (u) => CommercialService.editCommercial(u));
export const editCommercialPro = createServiceThunk(editCommercialProKey, (u) => CommercialService.editCommercialPro(u));
export const updateCommercial = createServiceThunk(updateCommercialKey, (u) => CommercialService.updateCommercial(u));

export const getCommercialAllList = createAsyncThunkWithCaching(
    commercialAllListKey,
    async (payload, thunkAPI) => {
        try {
            const response =
                await CommercialAllService.getCommercialAllList(payload);
            return response;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    },
);

export const getCommercialAll = createAsyncThunkWithCaching(
    commercialAllKey,
    async (id, thunkAPI) => {
        try {
            const response = await CommercialAllService.getCommercialAll(id);
            return response;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.message);
        }
    },
);

export const getCommercialNoAccountList = createServiceThunk(
    commercialNoAccountListKey,
    (p) => CommercialAllService.getCommercialNoAccountList(p),
);

export const getCommercialNoAccount = createServiceThunk(
    commercialNoAccountKey,
    (id) => CommercialAllService.getCommercialNoAccount(id),
);
